/**
 * @file     ListingHeroBento.tsx
 * @purpose  The listing photo hero. Desktop: a bento of up to 5 photos
 *           (2fr 1fr 1fr, two 228px rows, gap 8; outer corners 20, inner 12)
 *           with the media pills (Virtual tour, Street view, Map) bottom left
 *           and "Show all N photos" bottom right. Phone: ListingHeroCarousel.
 *           Every tile opens the lightbox at its index; each pill opens its
 *           tab. Without photos it leads with the map and street view, so the
 *           hero never sits empty. Photo 1 is the LCP image.
 */
'use client';

import { useState, type ReactNode } from 'react';
import Image from 'next/image';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import { useAppTheme } from '@/core/theme/useAppTheme';
import { mlsCardImageLoader, mlsHeroImageLoader } from '@/services/mls/mlsImageLoader';
import { fontWeightTokens, glassMaterial, layoutTokens, motionTokens } from '@/design-system/tokens';
import { propertyDetailContent as C } from '../../pageContent';
import type { DetailMedia } from '../../UnifiedDetail';
import { HERO_QUALITY, HERO_SIZES, type GalleryTab } from '../galleryModel';
import { GalleryIcon, type GalleryIconName } from '../galleryIcons';
import { ListingHeroCarousel } from './ListingHeroCarousel';

const R = layoutTokens.radius;
const ROW = 228;
const GAP = 8;
const { durations, easing } = motionTokens;

interface Props {
  media: DetailMedia[];
  titleAlt: string;
  statusChip?: ReactNode;
  sentinelId: string;
  /** Media tabs that exist for this listing, in pill order. */
  mediaTabs: Exclude<GalleryTab, 'photos'>[];
  onOpenPhoto: (index: number) => void;
  onOpenTab: (tab: Exclude<GalleryTab, 'photos'>) => void;
  onShowAll: () => void;
  /** Warm the lightbox chunk when the visitor shows intent. */
  onIntent?: () => void;
  /** Quick views: the carousel and thumb strip at every width, inside the host's column. */
  compact?: boolean;
}

const TAB_ICON: Record<Exclude<GalleryTab, 'photos'>, GalleryIconName> = { tour: 'tour', street: 'street', map: 'map' };

/** Grid placement per photo count, so 1 to 4 photos still fill the frame. */
function layoutFor(n: number): { columns: string; areas: string[] } {
  if (n >= 5) return { columns: '2fr 1fr 1fr', areas: ['1 / 1 / 3 / 2', '1 / 2 / 2 / 3', '1 / 3 / 2 / 4', '2 / 2 / 3 / 3', '2 / 3 / 3 / 4'] };
  if (n === 4) return { columns: '2fr 1fr 1fr', areas: ['1 / 1 / 3 / 2', '1 / 2 / 2 / 3', '1 / 3 / 2 / 4', '2 / 2 / 3 / 4'] };
  if (n === 3) return { columns: '2fr 1fr', areas: ['1 / 1 / 3 / 2', '1 / 2 / 2 / 3', '2 / 2 / 3 / 3'] };
  if (n === 2) return { columns: '2fr 1fr', areas: ['1 / 1 / 3 / 2', '1 / 2 / 3 / 3'] };
  return { columns: '1fr', areas: ['1 / 1 / 3 / 2'] };
}

export function ListingHeroBento({ media, titleAlt, statusChip, sentinelId, mediaTabs, onOpenPhoto, onOpenTab, onShowAll, onIntent, compact = false }: Props) {
  const { theme } = useAppTheme();
  const { t } = useTranslation();
  const tx = (ref: { key: string; defaultValue: string }, vars?: Record<string, unknown>) =>
    t(ref.key, { defaultValue: ref.defaultValue, ...vars });
  const c = theme.semanticColors;
  const total = media.length;
  // A page served from the ISR cache can outlive its listing's media (the
  // proxy then 404s). A failed photo keeps its slot on the neutral surface.
  const [failed, setFailed] = useState<ReadonlySet<string>>(new Set());
  const markFailed = (key: string) => setFailed((s) => (s.has(key) ? s : new Set(s).add(key)));

  const tabLabel = (k: Exclude<GalleryTab, 'photos'>) =>
    tx(k === 'tour' ? C.gallery.tabTour : k === 'street' ? C.gallery.tabStreet : C.gallery.tabMap);

  const focusRing = { '&:focus-visible': { outline: `2px solid ${c.interactiveFocusRing}`, outlineOffset: 2 } };
  const pillSx = (phone: boolean) => ({
    height: phone ? 32 : 40,
    pl: phone ? 2.5 : 3,
    pr: phone ? 3 : 3.5,
    gap: phone ? 1.5 : 2,
    borderRadius: `${R.pill}px`,
    ...glassMaterial('ultraThin', { interactive: true }),
    color: c.textPrimary,
    fontSize: phone ? 12.5 : 13.5,
    fontWeight: fontWeightTokens.semibold,
    whiteSpace: 'nowrap',
    fontVariantNumeric: 'tabular-nums',
    ...focusRing,
  });

  const pills = (phone: boolean) =>
    (phone ? mediaTabs.slice(0, 2) : mediaTabs).map((k) => (
      <ButtonBase key={k} onClick={() => onOpenTab(k)} sx={pillSx(phone)}>
        <GalleryIcon name={TAB_ICON[k]} size={phone ? 14 : 16} />
        {tabLabel(k)}
      </ButtonBase>
    ));

  const sentinel = (
    <Box id={sentinelId} aria-hidden="true" sx={{ position: 'absolute', bottom: 0, width: 1, height: 1, pointerEvents: 'none' }} />
  );

  const frame = compact
    ? ({ position: 'relative' } as const)
    : ({ maxWidth: 1200, mx: 'auto', px: { xs: 0, md: 6 }, pt: { xs: 0, md: 3 }, position: 'relative' } as const);

  if (total === 0) {
    const leadTab = mediaTabs.includes('map') ? 'map' : mediaTabs[0];
    const sideTab = mediaTabs.includes('street') && leadTab !== 'street' ? 'street' : null;
    const panelSx = {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      height: '100%',
      borderRadius: { xs: 0, md: `${R.sm}px` },
      bgcolor: c.surfaceElevated,
      color: c.textMuted,
      ...focusRing,
    } as const;
    const label = (text: string, icon: GalleryIconName) => (
      <Box component="span" sx={{ ...pillSx(false), position: 'absolute', left: 16, bottom: 16, display: 'inline-flex', alignItems: 'center', pointerEvents: 'none' }}>
        <GalleryIcon name={icon} size={16} />
        {text}
      </Box>
    );
    return (
      <Box sx={frame} onPointerEnter={onIntent}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: compact ? '1fr' : { xs: '1fr', md: sideTab ? '2fr 1fr' : '1fr' },
            gap: `${GAP}px`,
            height: compact ? 'auto' : { xs: 'auto', md: ROW * 2 + GAP },
            aspectRatio: compact ? '4 / 3' : { xs: '4 / 3', md: 'auto' },
            borderRadius: compact ? `${R.md}px` : { xs: 0, md: `${R.lg}px` },
            overflow: 'hidden',
          }}
        >
          {leadTab ? (
            <ButtonBase onClick={() => onOpenTab(leadTab)} aria-label={tabLabel(leadTab)} sx={panelSx}>
              <GalleryIcon name={TAB_ICON[leadTab]} size={32} />
              {label(tx(C.gallery.noPhotosYet), 'image')}
            </ButtonBase>
          ) : (
            <Box sx={panelSx}>
              <GalleryIcon name="image" size={32} />
              <Typography sx={{ position: 'absolute', left: 16, bottom: 16, fontSize: 13.5, fontWeight: fontWeightTokens.semibold, color: c.textSecondary }}>
                {tx(C.gallery.noPhotosYet)}
              </Typography>
            </Box>
          )}
          {sideTab ? (
            <ButtonBase onClick={() => onOpenTab(sideTab)} aria-label={tabLabel(sideTab)} sx={{ ...panelSx, display: compact ? 'none' : { xs: 'none', md: 'flex' } }}>
              <GalleryIcon name="street" size={32} />
              {label(tabLabel(sideTab), 'street')}
            </ButtonBase>
          ) : null}
        </Box>
        {sentinel}
      </Box>
    );
  }

  const { columns, areas } = layoutFor(total);
  const tiles = media.slice(0, areas.length);

  return (
    <Box sx={frame} onPointerEnter={onIntent} onFocus={onIntent}>
      <Box sx={{ display: compact ? 'block' : { xs: 'block', md: 'none' } }}>
        <ListingHeroCarousel
          compact={compact}
          media={media}
          titleAlt={titleAlt}
          failed={failed}
          onPhotoFailed={markFailed}
          onOpenPhoto={onOpenPhoto}
          onShowAll={onShowAll}
          pills={mediaTabs.length > 0 ? pills(true) : null}
          statusChip={statusChip}
        />
      </Box>

      {compact ? null : (
      <Box sx={{ display: { xs: 'none', md: 'block' }, position: 'relative' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: columns.split(' ').map((f) => `minmax(0, ${f})`).join(' '),
            gridTemplateRows: `${ROW}px ${ROW}px`,
            gap: `${GAP}px`,
            borderRadius: `${R.lg}px`,
            overflow: 'hidden',
          }}
        >
          {tiles.map((m, i) => (
            <ButtonBase
              key={m.key}
              onClick={() => onOpenPhoto(i)}
              aria-label={tx(C.gallery.openPhoto, { current: i + 1, total })}
              sx={{
                gridArea: areas[i],
                position: 'relative',
                display: 'block',
                overflow: 'hidden',
                borderRadius: `${R.sm}px`,
                bgcolor: c.surfaceElevated,
                ...(i > 0
                  ? {
                      '& img': { transition: `transform ${durations.slow}ms ${easing.standard}` },
                      '@media (hover: hover)': { '&:hover img': { transform: `scale(${motionTokens.interaction.photoZoom})` } },
                      '@media (prefers-reduced-motion: reduce)': { '& img': { transition: 'none' } },
                    }
                  : {}),
                '&:focus-visible': { outline: `2px solid ${c.interactiveFocusRing}`, outlineOffset: -4 },
              }}
            >
              {failed.has(m.key) ? (
                <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: c.textMuted }}>
                  <GalleryIcon name="image" size={24} />
                </Box>
              ) : (
                <Image
                  src={m.url}
                  loader={i === 0 ? mlsHeroImageLoader : mlsCardImageLoader}
                  alt={m.caption ? tx(C.gallery.photoAltCaption, { caption: m.caption, title: titleAlt }) : tx(C.gallery.photoAltAt, { current: i + 1, total, title: titleAlt })}
                  fill
                  priority={i === 0}
                  fetchPriority={i === 0 ? 'high' : undefined}
                  loading={i === 0 ? undefined : 'lazy'}
                  sizes={i === 0 ? HERO_SIZES : '(max-width: 1199px) 25vw, 284px'}
                  quality={i === 0 ? HERO_QUALITY : 75}
                  style={{ objectFit: 'cover' }}
                  onError={() => markFailed(m.key)}
                />
              )}
            </ButtonBase>
          ))}
        </Box>

        {statusChip ? <Box sx={{ position: 'absolute', top: 16, left: 16, zIndex: 1 }}>{statusChip}</Box> : null}
        {mediaTabs.length > 0 ? (
          <Box sx={{ position: 'absolute', left: 16, bottom: 16, display: 'flex', gap: 2, zIndex: 1 }}>{pills(false)}</Box>
        ) : null}
        {total > 1 ? (
          <ButtonBase onClick={onShowAll} sx={{ ...pillSx(false), position: 'absolute', right: 16, bottom: 16, zIndex: 1 }}>
            <GalleryIcon name="grid" size={16} />
            {tx(C.gallery.showAll, { count: total })}
          </ButtonBase>
        ) : null}
      </Box>
      )}
      {sentinel}
    </Box>
  );
}
